import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, homeForRole } from "@/context/AuthContext";
import { SignOut, ChartBar, Users, ForkKnife, ChefHat, Money, House } from "@phosphor-icons/react";

const ROLE_LABELS = {
  root: "Administrador",
  mesero: "Mesero",
  caja: "Caja",
  taquero1: "Taquero · Pastor",
  taquero2: "Taquero · Plancha",
  taquero3: "Taquero · Barra",
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const onLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  if (!user) return null;

  const navItems = [];
  if (user.role === "root") {
    navItems.push({ to: "/admin/root", label: "Dashboard", icon: <ChartBar size={20} weight="bold" /> });
    navItems.push({ to: "/admin/users", label: "Usuarios", icon: <Users size={20} weight="bold" /> });
    navItems.push({ to: "/admin/products", label: "Menú", icon: <ForkKnife size={20} weight="bold" /> });
    navItems.push({ to: "/admin/mesero", label: "Mesas", icon: <House size={20} weight="bold" /> });
    navItems.push({ to: "/admin/caja", label: "Caja", icon: <Money size={20} weight="bold" /> });
    navItems.push({ to: "/admin/taquero", label: "Cocina", icon: <ChefHat size={20} weight="bold" /> });
  } else {
    navItems.push({ to: homeForRole(user.role), label: ROLE_LABELS[user.role] || user.role, icon: <House size={20} weight="bold" /> });
  }

  return (
    <div className="min-h-screen bg-[var(--bg-pos)] flex flex-col">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="px-6 h-16 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Link to={homeForRole(user.role)} className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-brand text-white flex items-center justify-center font-display font-black">JR</div>
              <div className="font-display font-bold tracking-tight hidden sm:block">Taquería 360</div>
            </Link>
            <span className="hidden md:inline-block text-xs uppercase tracking-[0.2em] text-zinc-400 ml-3">/{location.pathname.split("/")[2] || "panel"}</span>
          </div>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((n) => {
              const active = location.pathname === n.to || (n.to !== "/admin" && location.pathname.startsWith(n.to));
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3.5 h-10 rounded-lg inline-flex items-center gap-2 text-sm font-semibold transition-colors ${
                    active ? "bg-brand text-white" : "text-zinc-600 hover:bg-zinc-100"
                  }`}
                >
                  {n.icon} {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold leading-tight">{user.name}</div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-500">{ROLE_LABELS[user.role] || user.role}</div>
            </div>
            <button onClick={onLogout} data-testid="admin-logout" className="w-10 h-10 rounded-lg border border-zinc-200 hover:border-brand hover:text-brand text-zinc-600 inline-flex items-center justify-center">
              <SignOut size={20} weight="bold" />
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {user.role === "root" && (
          <div className="md:hidden border-t border-zinc-200 px-3 py-2 overflow-x-auto pos-scroll flex gap-1">
            {navItems.map((n) => {
              const active = location.pathname === n.to;
              return (
                <Link key={n.to} to={n.to} className={`px-3 h-9 rounded-lg whitespace-nowrap inline-flex items-center gap-1.5 text-xs font-semibold ${active ? "bg-brand text-white" : "text-zinc-600 bg-zinc-100"}`}>
                  {n.icon} {n.label}
                </Link>
              );
            })}
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
